import debounce from "lodash/debounce";
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DraggableImageWrapper } from "src/components/atoms/DraggableImageWrapper";
import { useMaxView, usePageVisibility, useViewport } from "src/contexts";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { NavStep, ToggleComponent } from "src/pages/configurator/components";
import { Css } from "~generated/css";
import { isInsideMapsStagingMode, useLocalStorage } from "~utils";
import { InsideMapsCamera_InsideMapsCameraFragment } from "./InsideMaps.generated";
import { AlternateCamera, DEFAULT_CAMERA_NAME, InsideMapsData } from "./insideMapsData";

const FRAME_ORIGIN_PROD = "https://www.insidemaps.com";
const FRAME_ORIGIN_STAGING = "https://stage.insidemaps.com";
const FRAME_ORIGIN = isInsideMapsStagingMode() ? FRAME_ORIGIN_STAGING : FRAME_ORIGIN_PROD;
const SHOW_ALTERNATE_CAMERA_KEY = "configurator_alternateCameraView";

// When iframes post a message to their parent window, the `window.message` listener
// is called. Because we might have multiple iframes shown (i.e. one per room), we'd
// use a single global listener that uses the `event.source` (which will be the iframe's
// `contentWindow`) to route it back to "the React callback for this specific iframe".
const iframeRegistry = new Map<Window, (event: MessageEvent) => void>();

// Single global event listener for all iframe messages
let globalMessageListener: ((event: MessageEvent) => void) | null = null;

function setupGlobalMessageListener() {
  if (globalMessageListener || typeof window === "undefined") return;
  globalMessageListener = (event: MessageEvent) => {
    if (event.origin !== FRAME_ORIGIN) return;
    const handleMessage = event.source && iframeRegistry.get(event.source as Window);
    handleMessage?.(event);
  };
  window.addEventListener("message", globalMessageListener);
}

function cleanupGlobalMessageListener() {
  if (globalMessageListener && typeof window !== "undefined") {
    window.removeEventListener("message", globalMessageListener);
    globalMessageListener = null;
  }
}

// We don't want tests or chromatic trying to load the external iframe content
// Because they use different mocking techniques, we'll just use this global flag to keep things simple
let disableInsideMapsIframe = false;
export function setDisableInsideMapsIframe(value: boolean) {
  disableInsideMapsIframe = value;
}

/** Get the camera name for a given room location from the cameras array */
function getCameraForLocation(
  locationId: string | undefined,
  cameras: InsideMapsCamera_InsideMapsCameraFragment[],
): string {
  if (!locationId || cameras.length === 0) return DEFAULT_CAMERA_NAME;
  // Find a primary camera (all alt cameras are nested within `cam.alternates`) for this location
  const camera = cameras.find((cam) => cam.locationId === locationId);
  return camera?.cameraName ?? DEFAULT_CAMERA_NAME;
}

/** Get the alternate camera config for a primary camera */
function getAlternateCameraConfig(
  primaryCameraName: string,
  cameras: InsideMapsCamera_InsideMapsCameraFragment[],
): { displayName: string; alternateCameras: AlternateCamera[] } {
  const primaryCamera = cameras.find((cam) => cam.cameraName === primaryCameraName);
  if (primaryCamera) {
    return {
      displayName: primaryCamera.displayName,
      alternateCameras: primaryCamera.alternateCameras,
    };
  }
  return { displayName: primaryCameraName, alternateCameras: [] };
}

type InsideMapsIFrameProps = {
  roomLocationId: string | undefined;
  isPreview: boolean;
  // Each iframe must have a unique id to ensure messages are routed correctly back to the correct frame
  iframeId: string;
  hidePreviews?: boolean;
  hideAlternateCameraToggle?: boolean;
};

/**
 * InsideMaps iframe component that reads from ConfiguratorContext.
 * Use this within the configurator where ConfiguratorProvider is available.
 */
export function InsideMapsIFrame(props: InsideMapsIFrameProps) {
  const { insideMaps, checkoutConfig } = useConfiguratorContext();
  const { defaultInsideMapsRoomLocationId } = insideMaps;
  const { roomLocationId, hidePreviews = false, ...restProps } = props;
  // Only render the iFrame if we are visible to the user so we don't overload
  // inside maps WebGL context in the browser.
  const isVisible = usePageVisibility();

  // Use the first room location id as our default as these steps are already ordered by the `insideMapsRoomOrder` field
  const currentRoomLocationId = roomLocationId ?? defaultInsideMapsRoomLocationId;

  if (
    disableInsideMapsIframe ||
    !insideMaps.insideMapsProjectId ||
    !isVisible ||
    !currentRoomLocationId ||
    hidePreviews
  )
    return <InsideMapsIframeStub insideMaps={insideMaps} configIsLoaded={!!checkoutConfig} {...props} />;

  return (
    <InsideMapsIFrameView
      {...restProps}
      roomLocationId={currentRoomLocationId}
      insideMaps={insideMaps}
      /** Inside maps doesn't support swapping out the project (plan) as seamlessly as changing a camera, in this case
       * we need to just force a react remount of the component to load the frame from scratch. Note we currently don't
       * support changing the plan while displaying an inside maps iframe, but you can arrive at this state by changing the plan
       * then rapidly navigating back to the interior step while the mutation response is still in flight.
       */
      key={insideMaps.insideMapsProjectId}
    />
  );
}

export type InsideMapsIFrameViewProps = InsideMapsIFrameProps & {
  // Required prop for InsideMaps functionality
  insideMaps: InsideMapsData;
};

/**
 * InsideMaps iframe component that accepts insideMaps data as props.
 * Use this outside the configurator context (e.g., rendering capture page).
 */
export function InsideMapsIFrameView(props: InsideMapsIFrameViewProps) {
  const { roomLocationId: primaryRoomLocationId, isPreview, iframeId, hideAlternateCameraToggle, insideMaps } = props;
  const { insideMapsProjectId, insideMapsTextureInputs, cameras } = insideMaps;

  const { isMobile } = useViewport();
  const { isMaxViewOpen } = useMaxView();
  const [isLoaded, setIsLoaded] = useState(false);

  // Get the primary camera for this room location
  const primaryCameraName = useMemo(() => {
    return getCameraForLocation(primaryRoomLocationId, cameras);
  }, [primaryRoomLocationId, cameras]);

  // Stable iframe src that doesn't change when room location changes since we're changing cameras via `changeCamera` messages
  // but ensures we load up the correct camera initially
  const [iframeSrc] = useState(
    () => `${FRAME_ORIGIN}/app/web-overlay/?projectId=${insideMapsProjectId}&autoplay=true&camera=${primaryCameraName}`,
  );

  // Storing the user's preference allows us to open the correct camera in a max view
  const [isShowingAlternate, setIsShowingAlternate] = useLocalStorage(SHOW_ALTERNATE_CAMERA_KEY, false);

  // Get alternate camera configuration for this room
  const alternateCameraConfig = useMemo(() => {
    return getAlternateCameraConfig(primaryCameraName, cameras);
  }, [primaryCameraName, cameras]);

  // Get the current camera name (either primary or alternate)
  const currentCameraName = useMemo(() => {
    if (!isShowingAlternate) return primaryCameraName;

    const { alternateCameras } = alternateCameraConfig;

    // While the backend supports multiple alternate cameras for a room, we need a new `ToggleComponent` type UX to support more than 2 values
    if (alternateCameras.length > 1) console.error("Multiple alternate cameras are not supported yet");
    return alternateCameras.first?.cameraName ?? primaryCameraName;
  }, [primaryCameraName, isShowingAlternate, alternateCameraConfig]);

  // While the `currentCameraName` value is the intended camera to be shown (passed down & derived via the `roomLocationId` prop)
  // we must also track the known state of the iframe's camera to know when to send a changeCamera message
  // We use a ref here rather than state as we need a synchronous check to avoid race conditions with async
  // state updates that cause duplicate or out-of-sync calls to `changeCamera`
  const internalCurrentCameraNameRef = useRef(currentCameraName);

  // Create a single ref for the iframe
  const iframeRef = useMemo(() => createRef<HTMLIFrameElement>(), []);

  const { containerRef, sizing } = useOptimalIframeSizing();

  const textureInputs = useMemo(
    () => insideMapsTextureInputs[currentCameraName ?? DEFAULT_CAMERA_NAME] ?? [],
    [insideMapsTextureInputs, currentCameraName],
  );

  const initView = useCallback(() => {
    const message = {
      type: "initializeApp",
      cameraName: currentCameraName ?? DEFAULT_CAMERA_NAME,
      textures: textureInputs,
    };
    console.debug(`initializing frame: ${iframeId}`, { message });
    iframeRef.current?.contentWindow?.postMessage(message, FRAME_ORIGIN);
  }, [iframeId, currentCameraName, iframeRef, textureInputs]);

  const swapTextures = useCallback(
    (textureSwaps: { part: string; texture: string }[]) => {
      if (!iframeRef.current?.contentWindow || !isLoaded) {
        console.error(`Attempted to swap textures before iframe was loaded for frame: ${iframeId}`);
        return;
      }

      const message = {
        type: "swapMultipleTextures",
        iframeId,
        textures: textureSwaps,
      };

      console.debug(`Sending message to frame: ${iframeId}`, message);

      iframeRef.current.contentWindow.postMessage(message, FRAME_ORIGIN);
    },
    [iframeRef, isLoaded, iframeId],
  );

  // Respond to changes in texture swaps from the backend to send `swapTextures` calls to the frame
  useEffect(() => {
    if (!textureInputs || !isLoaded) return;
    if (textureInputs.nonEmpty) swapTextures(textureInputs);
  }, [textureInputs, currentCameraName, swapTextures, isLoaded]);

  const changeCamera = useCallback(
    (newCameraName: string) => {
      // Skip if we're already on this camera (prevents duplicate calls)
      // Only check the ref if the iframe is loaded - if not loaded, let the useEffect handle it
      // Use ref for synchronous check to prevent race conditions with async state updates
      if (isLoaded && internalCurrentCameraNameRef.current === newCameraName) return;

      // If the iframe isn't loaded yet, return early without updating anything
      // The useEffect will handle sending the camera change when `isLoaded` becomes true
      // This ensures pending camera changes are properly sent after initialization
      if (!isLoaded || !iframeRef.current?.contentWindow) {
        return;
      }

      // Iframe is loaded and ready - update ref and send the message
      internalCurrentCameraNameRef.current = newCameraName;

      const message = {
        type: "changeCamera",
        cameraName: newCameraName,
        textures: textureInputs,
      };
      console.debug(`sending camera change message for frame: ${iframeId}`, { message });
      iframeRef.current.contentWindow.postMessage(message, FRAME_ORIGIN);
    },
    [iframeId, isLoaded, iframeRef, textureInputs],
  );

  // Logging all event data posted back from the iframe for now
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data.type === "error") {
        // The main type of error we expect to get is when a material does not exist on the InsideMaps side
        // We will have to figure out what workflow makes sense and if we need to show anything to the user in that case
        console.error("Error from inside maps", event.data);
      } else {
        // Currently the only non-error event we care about is the `appInitialized`, which means
        // it's safe to start sending `swapTextures` or `changeCamera` messages to the iframe
        if (event.data.type === "appInitialized") {
          setIsLoaded(true);
          // On mobile devices, we sometimes find that the iFrame is initialized with the wrong camera so we catch that here and call a changeCamera if so.
          if (event.data.cameraName !== currentCameraName) {
            changeCamera(currentCameraName);
          }
        }
        console.debug(`Message from inside maps frame: ${iframeId}`, event.data);
      }
    },
    [iframeId, currentCameraName, changeCamera],
  );

  // Register this iframe instance with the global registry
  useEffect(() => {
    const currentIframe = iframeRef.current;
    if (currentIframe?.contentWindow) {
      iframeRegistry.set(currentIframe.contentWindow, handleMessage);
      setupGlobalMessageListener();
    }

    return () => {
      if (currentIframe?.contentWindow) {
        iframeRegistry.delete(currentIframe.contentWindow);
        // Clean up global listener if no more iframes exist
        if (iframeRegistry.size === 0) {
          cleanupGlobalMessageListener();
        }
      }
    };
  }, [iframeId, handleMessage, iframeRef]);

  // When the current camera prop changes (or the iframe becomes loaded with a stale camera selection),
  // update the camera in the iframe. `changeCamera` will update the ref once the post message is sent to the iframe
  // The check in `changeCamera` (using the ref) prevents duplicate calls to iframe, which can cause rendering issues
  useEffect(() => {
    if (currentCameraName && isLoaded && currentCameraName !== internalCurrentCameraNameRef.current) {
      changeCamera(currentCameraName);
    }
  }, [currentCameraName, changeCamera, isLoaded]);

  // When the primary room location changes, clear the alternate camera state and reset the iframe when we're hiding the toggle
  useEffect(() => {
    hideAlternateCameraToggle && setIsShowingAlternate(false);
  }, [primaryRoomLocationId, hideAlternateCameraToggle, setIsShowingAlternate]);

  /**
   * The iframe containers are set up to "crop" the rendered content to a 4:3 aspect ratio which the InsideMaps
   * renderings are designed for. We now dynamically calculate the optimal sizing based on the parent container dimensions
   * to minimize cropping while maintaining the 4:3 aspect ratio.
   */
  return (
    <>
      <div ref={containerRef} css={Css.f1.relative.bgWhite.oh.h100.w100.$}>
        {!isPreview && alternateCameraConfig?.alternateCameras.nonEmpty && !isMobile && (
          <div css={Css.z1.absolute.topPx(24).left("50%").add("transform", "translate(-50%)").$}>
            <ToggleComponent
              offLabel={alternateCameraConfig.displayName}
              onLabel={alternateCameraConfig.alternateCameras.first?.displayName ?? "Alternate View"}
              value={isShowingAlternate}
              onClick={() => setIsShowingAlternate(!isShowingAlternate)}
            />
          </div>
        )}
        <DraggableImageWrapper
          draggable={isMobile && isMaxViewOpen}
          contentStyle={Css.w(sizing.width).h(sizing.height).$}
        >
          <div
            css={
              Css.absolute.top0.left0
                .ar("4 / 3")
                // Dynamic sizing calculated to minimize cropping while maintaining 4:3 aspect ratio
                // Fallback to reasonable defaults while sizing is being calculated
                // If we're in maxView, let DraggableImageWrapper handle it
                .add("height", isMaxViewOpen ? "100%" : (sizing.height ?? "110%"))
                .add("width", isMaxViewOpen ? "100%" : (sizing.width ?? "100%"))
                .add("left", "50%")
                .add("top", "50%")
                .add("transform", "translate(-50%, -50%)")
                // Smooth transitions for height/width changes to prevent janky resizing
                .add("transition", "height 100ms ease-out, width 100ms ease-out")
                .if(isPreview).pointerNone.$
            }
          >
            {/* transparent overlay on mobile to allow scrolling in safari */}
            {isMobile && <div css={Css.absolute.add("inset", "0").z(10).bgTransparent.$} />}
            <iframe
              title="Rendering"
              ref={iframeRef}
              // Note: on Mobile we set pointerNone to prevent the iframe from blocking interactions with overlaid buttons or scrolling
              // As of now, this iframe is just decorative anyways so it's safe to do this.
              css={Css.h100.w100.ifMobile.pointerNone.$}
              id={isPreview ? `${currentCameraName}-preview-iframe` : "insidemaps-iframe"}
              src={iframeSrc}
              onLoad={initView}
              // Custom data attribute to help pdf-service determine if the iframe is ready for screenshot
              data-screenshot-ready={isLoaded}
            />
          </div>
        </DraggableImageWrapper>
      </div>
      {isMobile && alternateCameraConfig?.alternateCameras.nonEmpty && !hideAlternateCameraToggle && (
        // transition height to 0 instead of de-rendering when max view is open to keep things a little smoother but not by much because it's still an iframe
        <div css={Css.df.w100.hPx(52).bgWhite.jcc.add("transition", "all 0.3s ease-in-out").if(isMaxViewOpen).h0.$}>
          {/* Note: we currently only support one alternative camera so it's safe to just hardcode the array like so inline */}
          {[
            { displayName: alternateCameraConfig.displayName, cameraName: primaryCameraName },
            {
              displayName: alternateCameraConfig.alternateCameras.first?.displayName ?? "Alternate View",
              cameraName: alternateCameraConfig.alternateCameras.first?.cameraName ?? "Alternate View",
            },
          ].map(({ displayName, cameraName }, idx) => (
            <div
              key={cameraName}
              // attach on click to div for larger tap target
              onClick={() => {
                setIsShowingAlternate(idx === 1);
              }}
            >
              <NavStep menuTitle={displayName} isActive={cameraName === currentCameraName} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

type InsideMapsIframeStubProps = InsideMapsIFrameProps & {
  insideMaps: InsideMapsData;
  configIsLoaded?: boolean;
};

function InsideMapsIframeStub(props: InsideMapsIframeStubProps) {
  const { iframeId, roomLocationId, hidePreviews, insideMaps, configIsLoaded = true } = props;

  const message = useMemo(() => {
    // If we're intentionally hiding the preview, don't show anything since we're likely working around webGL context issues
    // and simply delaying loading the iframes
    if (hidePreviews) return "";
    if (!configIsLoaded) return "Loading configurations...";
    if (insideMaps.insideMapsProjectId && !roomLocationId) return "Room location not available";
    if (insideMaps.insideMapsProjectId) return "Mock Inside Maps Container";
    return "Visualizations have not been configured for the selected plan";
  }, [configIsLoaded, roomLocationId, insideMaps.insideMapsProjectId, hidePreviews]);

  return (
    <div css={Css.h100.w100.bgGray100.df.fdc.aic.jcsa.$} id={iframeId}>
      <div>{message}</div>
    </div>
  );
}

const TARGET_ASPECT_RATIO = 4 / 3; // 4:3 aspect ratio

type IframeSizing = {
  height: string;
  width: string;
};

// Custom hook to calculate optimal iframe sizing based on container dimensions resizing as close as possible to the 4:3 ratio while minimizing excess cropping
function useOptimalIframeSizing() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sizing, setSizing] = useState<IframeSizing>({
    height: "100%",
    width: "100%",
  });

  // Debounced callback to ensure we're not updating state too often when changing the viewport size
  const debouncedCalculateSizing = useMemo(
    () =>
      debounce((width: number, height: number) => {
        // Skip if dimensions are invalid
        if (width <= 0 || height <= 0) return;

        // Calculate the optimal sizing to maintain 4:3 aspect ratio
        // while minimizing cropping
        const containerAspectRatio = width / height;

        if (containerAspectRatio > TARGET_ASPECT_RATIO) {
          // Container is wider than 4:3 - we need to scale up height
          // to fill the width while maintaining aspect ratio
          const scaleFactor = width / (height * TARGET_ASPECT_RATIO);
          const scaledHeight = `${(scaleFactor * 100).toFixed(2)}%`;

          setSizing({
            height: scaledHeight,
            width: "100%",
          });
        } else {
          // Container is taller than 4:3 - we need to scale up width
          // to fill the height while maintaining aspect ratio
          const scaleFactor = (height * TARGET_ASPECT_RATIO) / width;
          const scaledWidth = `${(scaleFactor * 100).toFixed(2)}%`;

          setSizing({
            height: "100%",
            width: scaledWidth,
          });
        }
      }, 100),
    [],
  );

  // Set up ResizeObserver with debounced callback
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width, height } = entry.contentRect;

      debouncedCalculateSizing(width, height);
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      debouncedCalculateSizing.cancel(); // Cancel any pending debounced calls
    };
  }, [debouncedCalculateSizing]);

  return { containerRef, sizing };
}
